import React, { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface IParameter {
  Name?: string;
  Description?: string;
  Type?: string;
  Required?: boolean;
  DefaultValue?: unknown;
  Location?: string;
  Properties?: Record<string, IParameter>;
  Items?: IParameter;
  [key: string]: unknown;
}

interface CreateApiFormData {
  actionId?: string;
  name: string;
  description: string;
  method: string;
  endpoint: string;
  input_parameters: Record<string, IParameter>;
  output_parameters: Record<string, IParameter>;
  /** GraphQL flavor (method === 'GRAPHQL'): operation text + kind. */
  graphql_query?: string;
  graphql_operation_type?: 'query' | 'mutation';
}

interface CreateApiContextType {
  formData: CreateApiFormData;
  updateFormData: (data: Partial<CreateApiFormData>) => void;
  resetFormData: () => void;
  setFormData: (data: CreateApiFormData) => void;
}

const defaultFormData: CreateApiFormData = {
  actionId: undefined,
  name: '',
  description: '',
  method: 'GET',
  endpoint: '',
  input_parameters: {},
  output_parameters: {},
  graphql_query: '',
  graphql_operation_type: undefined,
};

const CreateApiContext = createContext<CreateApiContextType | undefined>(undefined);

export const CreateApiProvider = ({ children }: { children: ReactNode }) => {
  const [formData, setFormData] = useState<CreateApiFormData>(defaultFormData);

  const updateFormData = useCallback((data: Partial<CreateApiFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  }, []);

  const resetFormData = useCallback(() => {
    setFormData(defaultFormData);
  }, []);

  const setCompleteFormData = useCallback((data: CreateApiFormData) => {
    setFormData(data);
  }, []);

  const value = useMemo(
    () => ({
      formData,
      updateFormData,
      resetFormData,
      setFormData: setCompleteFormData,
    }),
    [formData, resetFormData, setCompleteFormData, updateFormData]
  );

  return <CreateApiContext.Provider value={value}>{children}</CreateApiContext.Provider>;
};

export const useCreateApiContext = () => {
  const context = useContext(CreateApiContext);
  if (!context) {
    throw new Error('useCreateApiContext must be used within CreateApiProvider');
  }
  return context;
};
